import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCustomerDevices,
  createCustomerDevice,
  createRepairJobRpc,
  fetchStaffProfiles,
  CreateRepairJobPayload,
} from '@/services/repairs';
import { fetchCustomers, quickCreateCustomer } from '@/services/sales';
import { fetchAvailableLaptops } from '@/services/laptops';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  Wrench,
  UserPlus,
  Plus,
  Search,
  Loader2,
  X,
} from 'lucide-react';

interface CreateRepairFormProps {
  onSuccess: (repairId: string) => void;
  onCancel: () => void;
}

const COMMON_ACCESSORIES = ['Charger / Adapter', 'Power Cable', 'Laptop Bag / Sleeve', 'Wireless Mouse', 'Original Box'];

export const CreateRepairForm: React.FC<CreateRepairFormProps> = ({ onSuccess, onCancel }) => {
  const queryClient = useQueryClient();

  // Customer State
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [newCustName, setNewCustName] = useState('');
  const [newCustPhone, setNewCustPhone] = useState('');
  const [custCreating, setCustCreating] = useState(false);

  // Device Source Toggle: 'customer_device' | 'inventory_laptop'
  const [deviceSource, setDeviceSource] = useState<'customer_device' | 'inventory_laptop'>('customer_device');
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [selectedLaptopId, setSelectedLaptopId] = useState('');
  const [laptopSearch, setLaptopSearch] = useState('');

  // New Customer Device Inline Form State
  const [showNewDeviceForm, setShowNewDeviceForm] = useState(false);
  const [devBrand, setDevBrand] = useState('');
  const [devModel, setDevModel] = useState('');
  const [devSerial, setDevSerial] = useState('');
  const [devCpu, setDevCpu] = useState('');
  const [devRam, setDevRam] = useState('');
  const [devStorage, setDevStorage] = useState('');
  const [devColour, setDevColour] = useState('');
  const [devCharger, setDevCharger] = useState(false);
  const [devAccessories, setDevAccessories] = useState('');
  const [devIntakeCond, setDevIntakeCond] = useState('');
  const [devPasswordHint, setDevPasswordHint] = useState('');
  const [devCreating, setDevCreating] = useState(false);

  // Repair Job Details
  const [reportedIssue, setReportedIssue] = useState('');
  const [priority, setPriority] = useState('normal');
  const [estimatedCompletion, setEstimatedCompletion] = useState('');
  const [estimatedLabour, setEstimatedLabour] = useState<number>(0);
  const [estimatedParts, setEstimatedParts] = useState<number>(0);
  const [assignedTo, setAssignedTo] = useState('');
  const [intakeNotes, setIntakeNotes] = useState('');
  const [accessoriesReceived, setAccessoriesReceived] = useState<string[]>([]);
  const [customAccessory, setCustomAccessory] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Queries
  const { data: customers = [] } = useQuery({
    queryKey: ['customers', 'select', customerSearch],
    queryFn: () => fetchCustomers(customerSearch),
  });

  const { data: customerDevices = [], refetch: refetchDevices } = useQuery({
    queryKey: ['customer-devices', customerId],
    queryFn: () => (customerId ? fetchCustomerDevices(customerId) : Promise.resolve([])),
    enabled: !!customerId && deviceSource === 'customer_device',
  });

  const { data: availableLaptops = [] } = useQuery({
    queryKey: ['laptops', 'available-for-repair', laptopSearch],
    queryFn: () => fetchAvailableLaptops(laptopSearch),
    enabled: deviceSource === 'inventory_laptop',
  });

  const { data: staffList = [] } = useQuery({
    queryKey: ['staff-profiles'],
    queryFn: () => fetchStaffProfiles(),
  });

  const toggleAccessory = (acc: string) => {
    setAccessoriesReceived((prev) =>
      prev.includes(acc) ? prev.filter((a) => a !== acc) : [...prev, acc]
    );
  };

  const handleAddCustomAccessory = (e: React.KeyboardEvent | React.MouseEvent) => {
    if ('key' in e && e.key !== 'Enter') return;
    e.preventDefault();
    if (!customAccessory.trim()) return;
    if (!accessoriesReceived.includes(customAccessory.trim())) {
      setAccessoriesReceived((prev) => [...prev, customAccessory.trim()]);
    }
    setCustomAccessory('');
  };

  // Quick Customer Creation
  const handleQuickCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustName.trim()) return;
    try {
      setCustCreating(true);
      const created = await quickCreateCustomer({ name: newCustName, phone: newCustPhone });
      setCustomerId(created.id);
      setShowQuickCustomer(false);
      setNewCustName('');
      setNewCustPhone('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    } catch (err) {
      setSubmitError(parseDatabaseError(err));
    } finally {
      setCustCreating(false);
    }
  };

  // Create Customer Device Inline
  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) {
      setSubmitError('Please select a customer first before registering a device.');
      return;
    }
    if (!devBrand.trim() || !devModel.trim()) {
      setSubmitError('Device Brand and Model are required.');
      return;
    }

    try {
      setDevCreating(true);
      const created = await createCustomerDevice({
        customer_id: customerId,
        brand: devBrand.trim(),
        model: devModel.trim(),
        serial_number: devSerial.trim() || undefined,
        cpu: devCpu.trim() || undefined,
        ram: devRam.trim() || undefined,
        storage: devStorage.trim() || undefined,
        colour: devColour.trim() || undefined,
        charger_received: devCharger,
        accessories_received: devAccessories.trim() || undefined,
        intake_condition: devIntakeCond.trim() || undefined,
        login_password_hint: devPasswordHint.trim() || undefined,
      });

      await refetchDevices();
      setSelectedDeviceId(created.id);
      setShowNewDeviceForm(false);
      setDevBrand('');
      setDevModel('');
      setDevSerial('');
    } catch (err) {
      setSubmitError(parseDatabaseError(err));
    } finally {
      setDevCreating(false);
    }
  };

  // Submit Repair Mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!customerId) throw new Error('Customer is required.');
      if (!reportedIssue.trim()) throw new Error('Reported issue is required.');

      if (deviceSource === 'customer_device' && !selectedDeviceId) {
        throw new Error('Please select or register a customer device.');
      }
      if (deviceSource === 'inventory_laptop' && !selectedLaptopId) {
        throw new Error('Please select an inventory laptop.');
      }

      const totalEstimated = (Number(estimatedLabour) || 0) + (Number(estimatedParts) || 0);

      const payload: CreateRepairJobPayload = {
        customer_id: customerId,
        reported_issue: reportedIssue.trim(),
        customer_device_id: deviceSource === 'customer_device' ? selectedDeviceId : null,
        inventory_laptop_id: deviceSource === 'inventory_laptop' ? selectedLaptopId : null,
        priority,
        estimated_completion: estimatedCompletion || null,
        estimated_charge: totalEstimated,
        estimated_labour: estimatedLabour || 0,
        estimated_parts: estimatedParts || 0,
        accessories_received: accessoriesReceived,
        intake_notes: intakeNotes.trim() || null,
        assigned_to: assignedTo || null,
      };

      return await createRepairJobRpc(payload);
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      onSuccess(res.repair_id);
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    createMutation.mutate();
  };

  const inputClass = "w-full px-3 py-1.5 bg-white border border-brand-border rounded text-xs focus:ring-2 focus:ring-brand-green";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pr-1 text-xs">
      {submitError && <ErrorAlert message={submitError} />}

      {/* Customer Selection */}
      <div className="bg-brand-bg p-3 rounded-lg border border-brand-border space-y-2">
        <div className="flex items-center justify-between">
          <label className="font-bold text-brand-black uppercase tracking-wider">
            1. Select Customer <span className="text-brand-red">*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowQuickCustomer(!showQuickCustomer)}
            className="inline-flex items-center space-x-1 text-brand-green font-semibold hover:underline"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>+ Quick Add Customer</span>
          </button>
        </div>

        {showQuickCustomer && (
          <div className="flex flex-col sm:flex-row gap-2 bg-white p-2 rounded border border-brand-border">
            <input
              placeholder="Customer Full Name *"
              value={newCustName}
              onChange={(e) => setNewCustName(e.target.value)}
              className="flex-1 px-2.5 py-1.5 border rounded text-xs"
            />
            <input
              placeholder="Phone Number"
              value={newCustPhone}
              onChange={(e) => setNewCustPhone(e.target.value)}
              className="w-40 px-2.5 py-1.5 border rounded text-xs"
            />
            <button
              type="button"
              onClick={handleQuickCustomer}
              disabled={custCreating || !newCustName.trim()}
              className="px-3 py-1.5 bg-brand-green text-white font-semibold rounded text-xs disabled:opacity-50"
            >
              {custCreating ? 'Saving...' : 'Save & Select'}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="relative">
            <input
              placeholder="Search customer by name or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-white border border-brand-border rounded text-xs"
            />
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-brand-muted" />
          </div>
          <select
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setSelectedDeviceId('');
            }}
            className="w-full px-3 py-1.5 bg-white border border-brand-border rounded text-xs font-semibold text-brand-black"
          >
            <option value="">-- Select Customer --</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.phone ? `(${c.phone})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Target Device Selection */}
      <div className="bg-brand-bg p-3 rounded-lg border border-brand-border space-y-3">
        <div className="flex items-center justify-between">
          <label className="font-bold text-brand-black uppercase tracking-wider">
            2. Target Device Type <span className="text-brand-red">*</span>
          </label>

          <div className="flex items-center space-x-2 bg-white p-1 rounded border border-brand-border">
            <button
              type="button"
              onClick={() => setDeviceSource('customer_device')}
              className={`px-3 py-1 text-xs font-semibold rounded ${
                deviceSource === 'customer_device' ? 'bg-brand-green text-white' : 'text-brand-muted'
              }`}
            >
              Customer Device
            </button>
            <button
              type="button"
              onClick={() => setDeviceSource('inventory_laptop')}
              className={`px-3 py-1 text-xs font-semibold rounded ${
                deviceSource === 'inventory_laptop' ? 'bg-brand-green text-white' : 'text-brand-muted'
              }`}
            >
              Shop Inventory Laptop
            </button>
          </div>
        </div>

        {/* Customer Device Mode */}
        {deviceSource === 'customer_device' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-brand-black">Select Customer's Device</label>
              {customerId && (
                <button
                  type="button"
                  onClick={() => setShowNewDeviceForm(!showNewDeviceForm)}
                  className="text-brand-green font-semibold hover:underline flex items-center space-x-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Register New Device</span>
                </button>
              )}
            </div>

            {!customerId ? (
              <p className="p-3 text-center text-brand-muted italic bg-white rounded border">
                Please select a customer above to view or register their devices.
              </p>
            ) : customerDevices.length === 0 && !showNewDeviceForm ? (
              <div className="p-4 text-center bg-white rounded border space-y-2">
                <p className="text-brand-muted">No registered devices found for this customer.</p>
                <button
                  type="button"
                  onClick={() => setShowNewDeviceForm(true)}
                  className="px-3 py-1.5 bg-brand-green text-white font-semibold rounded text-xs"
                >
                  + Register Customer Device
                </button>
              </div>
            ) : (
              <select
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                className="w-full px-3 py-1.5 bg-white border border-brand-border rounded font-semibold text-brand-black"
              >
                <option value="">-- Select Customer Device --</option>
                {customerDevices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.brand} {d.model} {d.serial_number ? `(S/N: ${d.serial_number})` : ''}
                  </option>
                ))}
              </select>
            )}

            {/* Inline New Device Registration Form */}
            {showNewDeviceForm && customerId && (
              <div className="p-3 bg-white border border-brand-green/40 rounded-lg space-y-2">
                <h5 className="font-bold text-brand-black text-xs uppercase border-b pb-1">
                  Register New Customer Device
                </h5>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <input placeholder="Brand *" value={devBrand} onChange={(e) => setDevBrand(e.target.value)} className={inputClass} />
                  <input placeholder="Model *" value={devModel} onChange={(e) => setDevModel(e.target.value)} className={inputClass} />
                  <input placeholder="Serial Number" value={devSerial} onChange={(e) => setDevSerial(e.target.value)} className={inputClass} />
                  <input placeholder="Colour" value={devColour} onChange={(e) => setDevColour(e.target.value)} className={inputClass} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input placeholder="CPU (Optional)" value={devCpu} onChange={(e) => setDevCpu(e.target.value)} className={inputClass} />
                  <input placeholder="RAM (Optional)" value={devRam} onChange={(e) => setDevRam(e.target.value)} className={inputClass} />
                  <input placeholder="Storage (Optional)" value={devStorage} onChange={(e) => setDevStorage(e.target.value)} className={inputClass} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <input placeholder="Accessories (bag, mouse...)" value={devAccessories} onChange={(e) => setDevAccessories(e.target.value)} className={inputClass} />
                  <input placeholder="Intake Condition (scratches...)" value={devIntakeCond} onChange={(e) => setDevIntakeCond(e.target.value)} className={inputClass} />
                  <input placeholder="Password / PIN Hint" value={devPasswordHint} onChange={(e) => setDevPasswordHint(e.target.value)} className={inputClass} />
                </div>
                <div className="flex items-center justify-between pt-1">
                  <label className="flex items-center space-x-1.5 font-semibold text-brand-black cursor-pointer">
                    <input type="checkbox" checked={devCharger} onChange={(e) => setDevCharger(e.target.checked)} className="rounded text-brand-green" />
                    <span>Charger Received</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleCreateDevice}
                    disabled={devCreating || !devBrand.trim() || !devModel.trim()}
                    className="px-3 py-1 bg-brand-green text-white font-semibold rounded disabled:opacity-50"
                  >
                    {devCreating ? 'Saving Device...' : 'Save & Select Device'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Shop Inventory Laptop Mode */}
        {deviceSource === 'inventory_laptop' && (
          <div className="space-y-2">
            <input
              placeholder="Search shop inventory laptops..."
              value={laptopSearch}
              onChange={(e) => setLaptopSearch(e.target.value)}
              className={inputClass}
            />
            <select
              value={selectedLaptopId}
              onChange={(e) => setSelectedLaptopId(e.target.value)}
              className="w-full px-3 py-1.5 bg-white border border-brand-border rounded font-semibold text-brand-black"
            >
              <option value="">-- Select Inventory Laptop --</option>
              {availableLaptops.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.stock_id} — {l.brand} {l.model} {l.serial_number ? `(S/N: ${l.serial_number})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Repair Job Details */}
      <div className="space-y-3 bg-white p-3 rounded-lg border border-brand-border">
        <h4 className="font-bold text-brand-black uppercase tracking-wider">
          3. Repair Job Details
        </h4>

        <div>
          <label className="block font-bold text-brand-black uppercase mb-1">
            Reported Issue / Symptoms <span className="text-brand-red">*</span>
          </label>
          <textarea
            rows={2}
            placeholder="e.g. No display, overheating, battery not charging..."
            value={reportedIssue}
            onChange={(e) => setReportedIssue(e.target.value)}
            className="w-full px-3 py-1.5 bg-brand-bg border border-brand-border rounded font-medium resize-none"
          />
        </div>

        {/* Accessories Checklist */}
        <div>
          <label className="block font-semibold text-brand-black uppercase mb-1">
            Accessories Received with Device
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {COMMON_ACCESSORIES.map((acc) => {
              const selected = accessoriesReceived.includes(acc);
              return (
                <button
                  key={acc}
                  type="button"
                  onClick={() => toggleAccessory(acc)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    selected
                      ? 'bg-green-100 text-green-800 border-green-300'
                      : 'bg-brand-bg text-brand-muted border-brand-border hover:text-brand-black'
                  }`}
                >
                  {selected ? '✓ ' : '+ '}
                  {acc}
                </button>
              );
            })}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Other accessory (type and press Add)..."
              value={customAccessory}
              onChange={(e) => setCustomAccessory(e.target.value)}
              onKeyDown={handleAddCustomAccessory}
              className="flex-1 px-2.5 py-1 border rounded text-xs"
            />
            <button
              type="button"
              onClick={handleAddCustomAccessory}
              className="px-3 py-1 bg-brand-bg border font-semibold rounded text-brand-black hover:bg-white"
            >
              Add
            </button>
          </div>

          {accessoriesReceived.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {accessoriesReceived.map((acc) => (
                <span
                  key={acc}
                  className="inline-flex items-center space-x-1 px-2 py-0.5 bg-green-50 text-green-800 border border-green-200 rounded text-[11px]"
                >
                  <span>{acc}</span>
                  <button
                    type="button"
                    onClick={() => toggleAccessory(acc)}
                    className="text-green-600 hover:text-green-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block font-semibold text-brand-black uppercase mb-1">Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputClass}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <label className="block font-semibold text-brand-black uppercase mb-1">Est. Completion</label>
            <input
              type="date"
              value={estimatedCompletion}
              onChange={(e) => setEstimatedCompletion(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block font-semibold text-brand-black uppercase mb-1">Est. Labour (PKR)</label>
            <input
              type="number"
              min={0}
              placeholder="0"
              value={estimatedLabour}
              onChange={(e) => setEstimatedLabour(Number(e.target.value) || 0)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block font-semibold text-brand-black uppercase mb-1">Est. Parts (PKR)</label>
            <input
              type="number"
              min={0}
              placeholder="0"
              value={estimatedParts}
              onChange={(e) => setEstimatedParts(Number(e.target.value) || 0)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-brand-black uppercase mb-1">Assigned Staff</label>
            <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className={inputClass}>
              <option value="">Unassigned</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-brand-black uppercase mb-1">Intake Notes</label>
            <input
              placeholder="e.g. Minor scratches on top cover, original power brick included..."
              value={intakeNotes}
              onChange={(e) => setIntakeNotes(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
        <button
          type="button"
          onClick={onCancel}
          disabled={createMutation.isPending}
          className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={createMutation.isPending || !customerId || !reportedIssue.trim()}
          className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white text-sm font-bold rounded-md hover:bg-brand-green-hover disabled:opacity-50 shadow-md"
        >
          {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          <span>{createMutation.isPending ? 'Creating Job...' : 'Create Repair Job'}</span>
        </button>
      </div>
    </form>
  );
};
